import Case from 'case';
import { Duration } from 'luxon';
import type { Contract } from 'validata';
import {
  asDate,
  isArray,
  isBoolean,
  isNumber,
  isObject,
  isString,
  maybeArray,
  maybeBoolean,
  maybeNumber,
  maybeObject,
  maybeString,
  StringFormat,
} from 'validata';
import { isUlid } from '../validation/index.js';
import type { Patrol } from './patrol.js';
import type { CheckedVehicle } from './vehicle.js';
import { checkedVehicleContract } from './vehicle.js';

export interface AddGuestRequest {
  email?: string;
  name: string;
  phone?: string;
}

export const addGuestRequestContract: Contract<AddGuestRequest> = {
  email: maybeString({ format: StringFormat.email() }),
  name: isString({ minLength: 1, trim: 'both' }),
  phone: maybeString(),
};

export interface Location {
  address?: string;
  latitude?: number;
  longitude?: number;
  notes?: string;
}

export const locationContract: Contract<Location> = {
  address: maybeString({ trim: 'both' }),
  latitude: maybeNumber(),
  longitude: maybeNumber(),
  notes: maybeString(),
};

export type StartPatrolRequest = {
  guestsPresent: string[]; // ids
  location?: Location;
  notes?: string;
  patrollersPresent: string[]; // ids
  policeEventNumber?: string;
  started: Date;
  vehicle?: {
    rego: string;
    startOdometer: number;
  };
};

export const startPatrolRequestContract: Contract<StartPatrolRequest> = {
  guestsPresent: isArray(isUlid()),
  location: maybeObject(locationContract),
  notes: maybeString({ trim: 'both' }),
  patrollersPresent: isArray(isUlid()),
  policeEventNumber: maybeString({
    transform: (value) => value.toUpperCase(),
    trim: 'both',
  }),
  started: asDate({
    maxFuture: Duration.fromISO('PT1H'),
    maxPast: Duration.fromISO('PT6H'),
  }),
  vehicle: maybeObject({
    rego: isString({
      maxLength: 6,
      minLength: 1,
      regex: /^[0-9a-zA-Z]+$/,
      transform: (value) => value.toUpperCase(),
      trim: 'both',
    }),
    startOdometer: isNumber(),
  }),
};

export interface EccLogOnRequest
  extends Omit<Patrol.EccLogOnNotification, 'request'> {}

export const eccLogOnRequestContract: Contract<Omit<EccLogOnRequest, 'email'>> =
  {
    hasPoliceRadio: isBoolean(),
    vehicle: maybeObject({
      color: isString({
        transform: (value) => Case.capital(value),
        trim: 'both',
      }),
      hasLivery: isBoolean(),
      make: isString({
        transform: (value) => Case.capital(value),
        trim: 'both',
      }),
      model: isString({
        transform: (value) => Case.capital(value),
        trim: 'both',
      }),
      rego: isString({
        maxLength: 6,
        minLength: 1,
        regex: /^[0-9a-zA-Z]+$/,
        transform: (value) => value.toUpperCase(),
        trim: 'both',
      }),
    }),
    when: asDate({
      maxFuture: Duration.fromObject({ minutes: 5 }),
      maxPast: Duration.fromObject({ minutes: 5 }),
    }),
  };

export interface FinishPatrolRequest {
  emailEcc?: boolean;
  finished: Date;
  guestsPresent: string[]; // ids
  location?: Location;
  notes?: string;
  patrollersPresent: string[]; // ids
  vehicle?: {
    finishOdometer: number;
    fuelLevel?: number;
  };
}

export const finishPatrolRequestContract: Contract<FinishPatrolRequest> = {
  emailEcc: maybeBoolean(),
  finished: asDate({ maxFuture: Duration.fromISO('PT15M') }),
  guestsPresent: isArray(isUlid()),
  location: maybeObject(locationContract),
  notes: maybeString({ trim: 'both' }),
  patrollersPresent: isArray(isUlid()),
  vehicle: maybeObject({
    finishOdometer: isNumber(),
    fuelLevel: maybeNumber({ max: 1, min: 0 }),
  }),
};

export interface AdminCompletePatrolRequest {
  finished: Date;
  guestsPresent: string[]; // ids
  notes?: string;
  patrollersPresent: string[]; // ids
  started: Date;
}

export const adminCompletePatrolRequestContract: Contract<AdminCompletePatrolRequest> =
  {
    finished: asDate({ maxFuture: Duration.fromISO('PT15M') }),
    guestsPresent: isArray(isUlid()),
    notes: maybeString({ trim: 'both' }),
    patrollersPresent: isArray(isUlid()),
    started: asDate({ maxFuture: Duration.fromISO('PT15M') }),
  };

export interface RecordIncidentRequest {
  location?: Location;
  people?: number;
  peopleDetails?: string;
  vehicles?: CheckedVehicle[];
  what: string;
  whatDetails?: string;
  when: Date;
}

export const recordIncidentRequestContract: Contract<RecordIncidentRequest> = {
  location: maybeObject(locationContract),
  people: maybeNumber(),
  peopleDetails: maybeString(),
  vehicles: maybeArray(isObject(checkedVehicleContract)),
  what: isString({ minLength: 3, trim: 'both' }),
  whatDetails: maybeString({ trim: 'both' }),
  when: asDate({ maxFuture: Duration.fromISO('PT1M') }),
};

export interface FinishIncidentRequest {
  whenFinished: Date;
}

export const finishIncidentRequestContract: Contract<FinishIncidentRequest> = {
  whenFinished: asDate({ maxFuture: Duration.fromISO('PT1M') }),
};

export interface QuickPatrolRequest {
  callSign: string;
  hours: number;
  name: string;
  patrollers?: string[]; // Additional patroller IDs (creator is auto-added)
  sendToEccLogOn?: boolean;
  when: Date;
}

export const quickPatrolRequestContract: Contract<QuickPatrolRequest> = {
  callSign: isString({
    minLength: 2,
    transform: (value) => value.toLocaleUpperCase(),
    trim: 'both',
  }),
  hours: isNumber({ min: 1 }),
  name: isString({ minLength: 3, trim: 'both' }),
  patrollers: isArray(isUlid(), { minLength: 1 }),
  sendToEccLogOn: maybeBoolean(),
  when: asDate({
    maxFuture: Duration.fromObject({ hours: 12 }),
    maxPast: Duration.fromObject({ minutes: -5 }),
  }),
};
